/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import cx from 'classnames';

import {
    useBreakpoint,
    Heading,
    Inline,
    Text,
    Placeholder,
    VerticalDivider,
    HeroImageUnavailable,
} from 'sarsaparilla';

import type { Photo } from './HeroImageGallery.types';

import styles from './HeroImageGalleryLayout.module.scss';

interface HeroImageGalleryLayoutSmProps {
    image?: Photo;
    showUnavailableImage?: boolean;
    nearText?: string;
    assetName?: string;
    parentText?: string;
}

export function HeroImageGalleryLayoutSm({
    image,
    showUnavailableImage = false,
    nearText,
    assetName,
    parentText,
}: HeroImageGalleryLayoutSmProps) {
    const { isSmAndDown } = useBreakpoint();

    let overlayTextInfo = null;
    if ((assetName || parentText || nearText) && isSmAndDown) {
        overlayTextInfo = (
            <div className={styles.overlay}>
                <Heading headingLevel={1} appearance="h5" className={styles.assetName}>
                    {assetName}
                </Heading>
                <Inline space="xs" className={styles.overlayInline}>
                    <Text size="sm" color="white">
                        {parentText}
                    </Text>
                    {nearText && <VerticalDivider />}
                    <Text size="sm" color="white">
                        {nearText}
                    </Text>
                </Inline>
            </div>
        );
    }

    if (showUnavailableImage) {
        return (
            <div className={cx(styles.layout, styles.sm)}>
                <HeroImageUnavailable />
                {overlayTextInfo}
            </div>
        );
    }

    // If we don't have a usable image
    if (!image?.url) {
        return (
            <div className={cx(styles.layout, styles.sm)}>
                <Placeholder height="100%" />
                {overlayTextInfo}
            </div>
        );
    }

    return (
        <div className={cx(styles.layout, styles.sm)}>
            <div
                className={cx(styles.photo, styles.hero)}
                style={{ backgroundImage: `url(${image.url})` }}
                role="img"
                aria-label={image.altText}
            />
            {overlayTextInfo}
        </div>
    );
}
